/**
 * ADA Accessibility Script - Optimized
 * Comprehensive accessibility improvements including:
 * - Removes empty HTML tags from product descriptions
 * - Fixes third-party app accessibility issues
 * - Cleans up alt text attributes
 */
(function () {
  "use strict";

  // ============================================================================
  // Constants
  // ============================================================================

  const MAX_ATTEMPTS = 200;
  const INTERVAL_TIME = 100;
  const SKIP_TAGS = new Set([
    "script",
    "style",
    "noscript",
    "iframe",
    "object",
    "embed",
  ]);
  const TAGS_TO_REMOVE = new Set([
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "p",
    "ul",
    "ol",
    "li",
    "a",
    "button",
    "label",
  ]);
  const GENERIC_WORDS =
    /\b(img|image|photo|picture|banner|icon|logo|graphic|placeholder|untitled|screenshot|pic)\b/gi;
  const FILE_EXT = /\.(jpe?g|png|gif|webp|svg|bmp|tiff?)$/gi;
  const CLEANUP = /^[-–—\s]+|[-–—\s]+$/g;

  const ENABLE_GORGIAS_CHAT = false;
  const ENABLE_YOTPO = false;
  const ENABLE_REBUY = false;
  const ENABLE_PS_WIDGET = true;

  // ============================================================================
  // Utility Functions
  // ============================================================================

  function pollUntilFound(
    selector,
    callback,
    root = document,
    maxAttempts = MAX_ATTEMPTS
  ) {
    let attempts = 0;
    const interval = setInterval(() => {
      if (attempts++ >= maxAttempts) {
        clearInterval(interval);
        return;
      }

      const element = root.querySelector ? root.querySelector(selector) : root;
      if (element && callback(element)) {
        clearInterval(interval);
      }
    }, INTERVAL_TIME);
    return interval;
  }

  function batchAttributeOperation(
    selector,
    operation,
    root = document,
    checkEmpty = false
  ) {
    return pollUntilFound(
      selector,
      () => {
        const elems = root.querySelectorAll(selector);
        if (elems.length === 0) return false;

        let updated = false;
        elems.forEach((elem) => {
          if (checkEmpty && elem.textContent.trim().length > 0) return;
          if (operation(elem)) updated = true;
        });

        return updated;
      },
      root
    );
  }

  // ============================================================================
  // Attribute Operations
  // ============================================================================

  function removeAttributes(
    selector,
    attributes,
    root = document,
    checkEmpty = false
  ) {
    batchAttributeOperation(
      selector,
      (elem) => {
        let updated = false;
        attributes.forEach((attr) => {
          if (elem.hasAttribute(attr)) {
            elem.removeAttribute(attr);
            updated = true;
          }
        });
        return updated;
      },
      root,
      checkEmpty
    );
  }

  function addAttributes(selector, attributes, root = document) {
    batchAttributeOperation(
      selector,
      (elem) => {
        let updated = false;
        Object.entries(attributes).forEach(([attr, value]) => {
          if (!elem.hasAttribute(attr)) {
            elem.setAttribute(attr, value);
            updated = true;
          }
        });
        return updated;
      },
      root
    );
  }

  function setAttributes(
    selector,
    attributes,
    root = document,
    checkEmpty = false
  ) {
    batchAttributeOperation(
      selector,
      (elem) => {
        let updated = false;
        Object.entries(attributes).forEach(([attr, value]) => {
          if (elem.getAttribute(attr) !== value) {
            elem.setAttribute(attr, value);
            updated = true;
          }
        });
        return updated;
      },
      root,
      checkEmpty
    );
  }

  // ============================================================================
  // Empty Tag Removal
  // ============================================================================

  function isEmpty(element) {
    return !element.textContent?.trim();
  }

  function removeEmptyTags(container) {
    if (!container) return;

    const elements = Array.from(container.querySelectorAll("*")).reverse();

    elements.forEach((element) => {
      const tagName = element.tagName.toLowerCase();
      if (SKIP_TAGS.has(tagName)) return;

      if (isEmpty(element)) {
        if (TAGS_TO_REMOVE.has(tagName)) {
          element.remove();
        }
      }
    });

    container.querySelectorAll("ul, ol").forEach((list) => {
      const items = list.querySelectorAll("li");
      if (items.length === 0 || Array.from(items).every((li) => isEmpty(li))) {
        list.remove();
      }
    });

    document.querySelectorAll("h2").forEach((h2) => {
      if (isEmpty(h2)) {
        h2.remove();
      }
    });
  }

  function cleanProductDescriptions() {
    const selectors = [
      ".main-description",
      ".enhanced-description",
      ".product_description_inner",
      ".details_main_list"
    ];

    const containers = new Set();
    const productSectionClasses = [
      ".section_product_main_new",
      ".section_product_main",
      ".section_product_description",
      ".block_product_details",
    ];

    selectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((el) => {
        const inProductSection = productSectionClasses.some((sectionClass) =>
          el.closest(sectionClass)
        );
        if (inProductSection || el.textContent.trim().length > 0) {
          containers.add(el);
        }
      });
    });

    containers.forEach(removeEmptyTags);
  }

  function cleanFAQPage() {
    const faqSelectors = [
      ".type_Accordion .accordion-panel",
    ];

    const containers = new Set();

    faqSelectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((el) => {
        containers.add(el);
      });
    });

    containers.forEach(removeEmptyTags);
  }

  // ============================================================================
  // Alt Text Cleanup
  // ============================================================================

  function processAltText() {
    // Add missing alt attributes
    document
      .querySelectorAll("img:not([alt])")
      .forEach((img) => img.setAttribute("alt", ""));

    // Clean existing alt text
    document.querySelectorAll("img[alt]").forEach((img) => {
      const original = img.getAttribute("alt");
      if (!original?.trim()) return;

      const cleaned = original
        .replace(/_/g, " ")
        .replace(GENERIC_WORDS, "")
        .replace(FILE_EXT, "")
        .replace(/\s+/g, " ")
        .replace(CLEANUP, "")
        .trim();

      if (cleaned !== original && cleaned) {
        img.setAttribute("alt", cleaned);
      }
    });
  }

  function lazyLoadImages() {
    document.querySelectorAll(".xs-image img").forEach((img) => {
      img.setAttribute("loading", "lazy");
      img.setAttribute("width", "220");
      img.setAttribute("height", "220");
    });

    document.querySelectorAll(".xs-image img + img").forEach((img) => {
      img.setAttribute("alt", "");
    });
  }

  function lazyLoadImagesInJudgemeReviews() {
    document.querySelectorAll(".jdgm-rev__pic-img").forEach((img) => {
      img.setAttribute("loading", "lazy");
      img.setAttribute("width", "90");
      img.setAttribute("height", "120");
    });
  }

  // Provide real text/ARIA for Judge.me badges and stars (which rely on CSS content)
  function fixJudgemeAccessibility() {
    pollUntilFound(".jdgm-widget, .jdgm-review-widget", () => {
      const ensureFallbackText = (element, text, key, visuallyHidden = false) => {
        if (!element || !text) return;

        const fallbackSelector = `.ada-judgeme-fallback[data-ada-text-key="${key}"]`;
        if (element.querySelector(fallbackSelector)) return;

        const hasReadableText = (element.textContent || "").trim().length > 0;
        if (hasReadableText) return;

        const span = document.createElement("span");
        span.className = visuallyHidden
          ? "visually-hidden ada-judgeme-fallback"
          : "ada-judgeme-fallback";
        span.setAttribute("data-ada-text-key", key);
        span.textContent = text;
        element.appendChild(span);
      };

      const applyJudgemeA11yFixes = () => {
        const scopes = document.querySelectorAll(
          ".jdgm-widget, .jdgm-review-widget"
        );
        if (!scopes.length) return false;

        scopes.forEach((scope) => {
          scope.querySelectorAll(".jdgm-rev__buyer-badge").forEach((badge) => {
            const labelText = "Verified buyer";
            ensureFallbackText(badge, labelText, "buyer-badge", false);
          });

          scope.querySelectorAll(".jdgm-rev__replier").forEach((element) => {
            if (!element.hasAttribute("aria-label")) {
              const replierName = (element.getAttribute("data-replier") || "")
                .trim()
                .replace(/\s+/g, " ");
              if (replierName) element.setAttribute("aria-label", replierName);
            }
            ensureFallbackText(
              element,
              element.getAttribute("aria-label") || "Brand reply",
              "replier"
            );
          });

          scope
            .querySelectorAll(
              ".jdgm-rev__prod-link-prefix, .jdgm-rev__variant-label, .jdgm-rev__out-of-store-text"
            )
            .forEach((element) => {
              const className = element.className || "";
              if (className.includes("jdgm-rev__prod-link-prefix")) {
                ensureFallbackText(element, "about", "prod-link-prefix");
              } else if (className.includes("jdgm-rev__variant-label")) {
                ensureFallbackText(element, "Variant:", "variant-label");
              } else if (className.includes("jdgm-rev__out-of-store-text")) {
                ensureFallbackText(element, "(out of store)", "out-of-store");
              }
            });

          scope.querySelectorAll(".jdgm-star").forEach((star) => {
            star.setAttribute("aria-hidden", "true");
            star.setAttribute("role", "presentation");
          });

          scope
            .querySelectorAll(".jdgm-form-dynamic__rating .jdgm-star")
            .forEach((star) => {
              const scoreText = (star.getAttribute("data-alt") || "").trim();
              if (!scoreText) return;
              const label = `${scoreText} star${scoreText === "1" ? "" : "s"}`;
              star.setAttribute("aria-label", label);
              star.setAttribute("role", "button");
              star.removeAttribute("aria-hidden");
            });

          scope.querySelectorAll(".jdgm-paginate__page").forEach((page) => {
            if (page.classList.contains("jdgm-paginate__next-page")) {
              page.setAttribute("aria-label", "Next page");
            } else if (page.classList.contains("jdgm-paginate__last-page")) {
              page.setAttribute("aria-label", "Last page");
            } else {
              const text = (page.textContent || "").trim();
              const dataPage = (page.getAttribute("data-page") || "").trim();
              if (!text && dataPage) {
                page.setAttribute("aria-label", `Page ${dataPage}`);
              }
            }
          });

          addSpanToBlankLinks(scope);

          scope
            .querySelectorAll(
              ".jdgm-preview-badge, .jdgm-rev-widg"
            )
            .forEach((container) => {
              let ratingLabel = "";

              if (container.classList.contains("jdgm-rev-widg")) {
                const avgAttr = Number(
                  container.getAttribute("data-average-rating") || ""
                );
                const reviewCountAttr = Number(
                  container.getAttribute("data-number-of-reviews") || ""
                );

                if (!Number.isNaN(avgAttr) && avgAttr > 0) {
                  ratingLabel = `Average rating ${avgAttr.toFixed(
                    2
                  )} out of 5 stars`;
                  if (!Number.isNaN(reviewCountAttr) && reviewCountAttr >= 0) {
                    ratingLabel = `${ratingLabel}, based on ${reviewCountAttr} reviews`;
                  }
                }
                container.setAttribute("role", "group");
              } else {
                const stars = container.querySelectorAll(".jdgm-star");
                if (stars.length) {
                  const rated = container.querySelectorAll(".jdgm-star.jdgm--on")
                    .length;
                  ratingLabel = `Rated ${rated} out of ${stars.length} stars`;
                  container.setAttribute("role", "img");
                }
              }

              if (!ratingLabel) return;

              container.setAttribute("aria-label", ratingLabel);
            });
        });

        return true;
      };

      applyJudgemeA11yFixes();

      if (document.body && !document.body.hasAttribute("data-ada-judgeme-observer")) {
        document.body.setAttribute("data-ada-judgeme-observer", "true");
        let isScheduled = false;
        const scheduleJudgemeFixes = () => {
          if (isScheduled) return;
          isScheduled = true;
          requestAnimationFrame(() => {
            isScheduled = false;
            applyJudgemeA11yFixes();
          });
        };

        new MutationObserver((mutations) => {
          const hasJudgemeMutation = mutations.some((mutation) => {
            const target = mutation.target;
            if (target?.nodeType === 1) {
              if (
                target.closest?.(".jdgm-widget, .jdgm-review-widget") ||
                target.classList?.contains("jdgm-widget") ||
                target.classList?.contains("jdgm-review-widget")
              ) {
                return true;
              }
            }

            if (mutation.type === "childList") {
              const allNodes = [
                ...Array.from(mutation.addedNodes || []),
                ...Array.from(mutation.removedNodes || []),
              ];
              return allNodes.some(
                (node) =>
                  node.nodeType === 1 &&
                  (node.classList?.contains("jdgm-widget") ||
                    node.classList?.contains("jdgm-review-widget") ||
                    /jdgm-/.test(node.className || "") ||
                    node.querySelector?.('[class*="jdgm-"]'))
              );
            }

            return false;
          });

          if (hasJudgemeMutation) {
            scheduleJudgemeFixes();
          }
        }).observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: [
            "class",
            "data-score",
            "data-average-rating",
            "data-number-of-reviews",
          ],
        });
      }

      return true;
    });
  }

  // Add visually hidden notice for links that open in a new tab
  function addSpanToBlankLinks(selectors) {
    const selectorList = Array.isArray(selectors) ? selectors : [selectors];

    selectorList.forEach((selector) => {
      const roots =
        selector && typeof selector === "string"
          ? document.querySelectorAll(selector)
          : [document];

      roots.forEach((root) => {
        if (!root) return;
        root.querySelectorAll('a[target="_blank"]').forEach((link) => {
          const hasHiddenNotice = Array.from(
            link.querySelectorAll(".visually-hidden")
          ).some((span) =>
            /\bopens in a new tab\b/i.test(span.textContent || "")
          );
          if (hasHiddenNotice) return;

          const hiddenSpan = document.createElement("span");
          hiddenSpan.className = "visually-hidden";
          hiddenSpan.textContent = " (opens in a new tab)";
          link.appendChild(hiddenSpan);
        });
      });
    });
  }

  // Add Badge in the visually hidden
  function addProductBadgeLabel() {
    const productCardAtcs = document.querySelectorAll('product-card-atc');

    if(!productCardAtcs.length) return;

    productCardAtcs.forEach(productCardAtc => {
      const badges = productCardAtc.querySelectorAll('.product-badges .badge:not(.hide)');
      const ariaDiv = productCardAtc.querySelector('.product-card-atc-aria:not(.is-aria-rendered)');
      let updatedAria = ariaDiv.innerText;

      if(!badges.length || !ariaDiv) return;

      badges.forEach(badge => {
        updatedAria = `${badge.innerText} ${updatedAria}`;
      })

      ariaDiv.classList.add('is-aria-rendered');
      ariaDiv.innerText = updatedAria;
    })
  }

  // ============================================================================
  // Third-Party App Fixes: Shopify
  // ============================================================================

  function fixShopifyVideoPlayers() {
    document
      .querySelectorAll(".js-video-player-remove-image")
      .forEach((videoPlayer) => {
        videoPlayer.removeAttribute("aria-label");
        videoPlayer.setAttribute("title", "Embedded Video Player");

        const img = videoPlayer.querySelector("img");
        if (img) {
          img.setAttribute("loading", "lazy");
          img.setAttribute("alt", "");
          img.setAttribute("aria-hidden", "true");
          img.setAttribute("role", "presentation");
        }
      });
  }

  function fixGorgiasChat() {
    pollUntilFound("#gorgias-chat-container", (container) => {
      container.setAttribute("aria-hidden", "true");
      const chatButton = container.querySelector("#chat-button");
      if (chatButton) chatButton.removeAttribute("role");
      return !!chatButton;
    });
  }

  // ============================================================================
  // Third-Party App Fixes: Klaviyo
  // ============================================================================

  function fixKlaviyoPopup() {
    pollUntilFound(".kl-teaser-XHV8vb", (popup) => {
      popup.setAttribute("aria-hidden", "true");
      return true;
    });
  }

  function fixPsWidgetContainer() {
    pollUntilFound("#ps__widget_container", (container) => {
      container.setAttribute("aria-hidden", "true");
      return true;
    });
  }

  // ============================================================================
  // Third-Party App Fixes: Yotpo
  // ============================================================================

  function fixYotpoReviews() {
    removeAttributes(
      ".yotpo-review-title",
      ["role", "aria-level"],
      document,
      true
    );
    removeAttributes(".yotpo-reviews-pagination-item", ["href"]);
  }

  function fixYotpoIcons() {
    addAttributes(
      ".yotpo-star-rating-icon, .yotpo-verified-icon, .yotpo-reviews-pagination-item svg",
      {
        "aria-hidden": "true",
        role: "presentation",
        focusable: "false",
      }
    );
  }

  // ============================================================================
  // Third-Party App Fixes: Rebuy
  // ============================================================================

  function setRebuyImageAriaLabel() {
    const updateAriaLabels = () => {
      const imageWrappers = document.querySelectorAll(
        ".rebuy-product-grid .rebuy-product-image"
      );
      if (!imageWrappers.length) return false;

      let updated = false;
      imageWrappers.forEach((imageWrapper) => {
        imageWrapper
          .querySelectorAll("img")
          .forEach((img) => img.setAttribute("alt", ""));

        const mediaParent = imageWrapper.closest(".rebuy-product-block");
        const infoSibling = mediaParent?.querySelector(".rebuy-product-info a");
        const label = infoSibling?.getAttribute("aria-label");

        if (label && imageWrapper.getAttribute("aria-label") !== label) {
          imageWrapper.setAttribute("aria-label", label);
          updated = true;
        }
      });

      return updated;
    };

    pollUntilFound(".rebuy-product-grid", (grid) => {
      updateAriaLabels();
      new MutationObserver(updateAriaLabels).observe(grid, {
        childList: true,
        subtree: true,
      });
      return true;
    });
  }

  function fixRebuyProducts() {
    removeAttributes(
      "a.rebuy-product-title, a.rebuy-cart__flyout-item-product-title",
      ["role", "aria-level"]
    );
    setAttributes(".rebuy-product-grid .rebuy-product-image img", {
      loading: "lazy",
      alt: "",
      "aria-hidden": "true",
    });
    setRebuyImageAriaLabel();
  }

  // ============================================================================
  // Shadow DOM Handlers
  // ============================================================================

  function handleShopCartSyncShadowRoot() {
    pollUntilFound("shop-cart-sync", (element) => {
      if (element.shadowRoot) {
        const iframe = element.shadowRoot.querySelector("iframe");
        if (iframe) {
          iframe.setAttribute("title", "Shop Analytics");
          return true;
        }
      }
      return false;
    });
  }

  function handlePaymentTermsShadowRoot() {
    pollUntilFound("shopify-payment-terms", (element) => {
      if (element.shadowRoot) {
        addAttributes(
          "#shopify-installments-content svg",
          {
            "aria-hidden": "true",
            role: "presentation",
            focusable: "false",
          },
          element.shadowRoot
        );
        return true;
      }
      return false;
    });
  }

  // ============================================================================
  // Initialization
  // ============================================================================

  // Immediate fixes (run right away, no polling needed)
  function runImmediateFixes() {
    cleanProductDescriptions();
    cleanFAQPage();
    fixShopifyVideoPlayers();
    processAltText();
    lazyLoadImages();
    lazyLoadImagesInJudgemeReviews();
    addSpanToBlankLinks("#shopify-pc__banner");
    addProductBadgeLabel();
  }

  // Initialize on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", runImmediateFixes);
  } else {
    runImmediateFixes();
  }

  // Delayed run for dynamic content
  setTimeout(runImmediateFixes, 500);

  // Polling fixes (handle their own retries)
  fixKlaviyoPopup();
  if (ENABLE_PS_WIDGET) fixPsWidgetContainer();
  fixJudgemeAccessibility();

  if (ENABLE_GORGIAS_CHAT) fixGorgiasChat();
  if (ENABLE_YOTPO) fixYotpoReviews();
  if (ENABLE_YOTPO) fixYotpoIcons();

  if (ENABLE_REBUY) fixRebuyProducts();

  addAttributes("#li_sync_frame", { title: "Sync Container" });
  handleShopCartSyncShadowRoot();
  handlePaymentTermsShadowRoot();

  // Alt text observer for dynamically added images and FAQ content
  let debounceTimer;
  new MutationObserver((mutations) => {
    const hasImages = mutations.some((m) =>
      Array.from(m.addedNodes).some(
        (n) =>
          n.nodeType === 1 && (n.tagName === "IMG" || n.querySelector?.("img"))
      )
    );
    const hasFAQContent = mutations.some((m) =>
      Array.from(m.addedNodes).some(
        (n) =>
          n.nodeType === 1 &&
          (n.classList?.contains("accordion-panel") ||
            n.querySelector?.(".accordion-panel") ||
            n.tagName === "UTIL-ACCORDION")
      )
    );
    if (hasImages) {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(processAltText, 200);
      lazyLoadImages();
      lazyLoadImagesInJudgemeReviews();
    }
    if (hasFAQContent) {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(cleanFAQPage, 200);
    }
  }).observe(document.body, { childList: true, subtree: true });
})();
